import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Flag from 'LuxKit/Flag'
import config from 'constants/config'
import { buildRegionSwitchSearch } from 'lib/url/searchUrlUtils'
import setRegionCookie from 'cookies/setRegionCookie'
import useToggle from 'hooks/useToggle'
import type { MouseEvent } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import RegionListItem from './RegionListItem'
import { REGION_DROPDOWN_START_ORDER } from 'constants/config/region'
import { partitionBy } from 'lib/array/arrayUtils'

const priorityRegionCodes = new Set(REGION_DROPDOWN_START_ORDER)

interface Props {
  location: any
  currentRegionCode: string
  currentRegionName: string
  className?: string
}

function NavigationHeaderRegionMenu(props: Props) {
  const intl = useIntl()
  const {
    currentRegionCode,
    location,
    className,
  } = props

  const { value: isOpen, toggle: toggleDropdown, off: closeDropdown } = useToggle(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const handleRegionClick = useCallback((region: App.RegionSummary, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (region.code !== currentRegionCode) {
      setRegionCookie(region.code)
      window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + region.code.toLowerCase()) + buildRegionSwitchSearch(location.search)
    }
  }, [currentRegionCode, location.pathname, location.search])

  const selectedRegion = useMemo(() => config.regions.find(region => (region.code === currentRegionCode)), [currentRegionCode])
  const selectedRegionFlagToDisplay = currentRegionCode === 'NL' ? 'EU' : currentRegionCode

  const [priorityRegions, otherRegions] = useMemo(() => partitionBy(config.regions, region => priorityRegionCodes.has(region.code)), [])

  if (config.regions.length < 2) {
    return null
  }

  return (<>
    <TextButton
      className={className}
      kind="tertiary"
      variant="default"
      aria-label={intl.formatMessage({
        id: 'JZtCc4y-bWz5UMS6Mrwx',
        defaultMessage: 'Select your region',
      })}
      aria-expanded={isOpen}
      aria-haspopup
      onClick={toggleDropdown}
      ref={triggerRef}
      startIcon={selectedRegion ? <Flag countryCode={selectedRegionFlagToDisplay} /> : undefined}
      endIcon={selectedRegion ? <DropdownChevron open={isOpen} /> : undefined}
    >
      {selectedRegion?.currencyCode ?? currentRegionCode}
    </TextButton>
    <DropdownList
      id="region-menu-dropdown"
      size="S"
      open={isOpen}
      onClose={closeDropdown}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
    >
      {priorityRegions.map(region => <RegionListItem
        key={region.code}
        region={region}
        onRegionClick={handleRegionClick}
      />)}
      <Divider kind="secondary" />
      {otherRegions.map(region => <RegionListItem
        key={region.code}
        region={region}
        onRegionClick={handleRegionClick}
      />)}
    </DropdownList>
  </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    location: state.router.location,
    currentRegionCode: state.geo.currentRegionCode,
    currentRegionName: state.geo.currentRegionName,
  }
}

export default connect(mapStateToProps)(NavigationHeaderRegionMenu)
